// @ts-check
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactSignaturePad from 'react-signature-canvas';
import { X_md as XIcon, Pencil_sm as PencilIcon } from '../Heroicons';
import { VerticalField } from '../FieldStructure';
import Modal, { ModalHeader } from '../Modal';
import { InputText } from '../InputText';
import { trimCanvas } from './trimCanvas';

/* eslint-disable max-len */

/**
 * Tab.
 */

/** @typedef {{
 active: boolean,
 onClick: () => void,
}} TabProps */

/** @type { React.ValidationMap<TabProps> } */
const tabPropTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

/** @type { React.SFC<TabProps> } */
const Tab = props => {
  const { active, onClick, children } = props;

  return (
    <button
      role="tab"
      aria-selected={active}
      className={`border border-transparent font-semibold hover:bg-blue-100 hover:shadow-inner inline-block px-3 px-6 py-1 rounded rounded-full text-blue-500 focus:outline-none border-blue-700 shadow-outline-blue ${
        active ? 'shadow-inner' : ''
      }`}
      style={
        active
          ? {
              borderColor: '#fff !important',
              backgroundColor: '#bee3f8',
              color: '#2b6cb0',
            }
          : undefined
      }
      onClick={onClick}
    >
      {children}
    </button>
  );
};

Tab.propTypes = tabPropTypes;

/**
 * TypeSignature.
 */

/** @typedef {{
 onSave: (base64png: string) => void,
 onCancel: () => void
}} TypeSignatureProps */

/** @typedef {{
 typedName: string
}} TypedSignatureState */

/** @type { React.ValidationMap<TypeSignatureProps> } */
const typedSignaturePropTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/** @augments { Component<TypeSignatureProps, TypedSignatureState> } */
class TypeSignature extends Component {
  static propTypes = typedSignaturePropTypes;

  constructor(props) {
    super(props);
    this.state = { typedName: '' };
    this.setSigPadRef = this.setSigPadRef.bind(this);
    this.updateCanvasSignature = this.updateCanvasSignature.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleInput = this.handleInput.bind(this);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.typedName !== prevState.typedName) {
      this.updateCanvasSignature(this.state.typedName);
    }
  }

  updateCanvasSignature(typedName) {
    if (this.sigPad) {
      const context = this.sigPad.getContext('2d');
      if (context) {
        // clear the text
        context.clearRect(0, 0, this.sigPad.width, this.sigPad.height);

        if (typedName.trim() !== '') {
          fitTextOnCanvas(this.sigPad, typedName, 'cursive');
        }
      }
    }
  }

  setSigPadRef(canvasRef) {
    this.sigPad = canvasRef;
  }

  handleSave() {
    const { onSave } = this.props;

    if (this.sigPad) {
      const copy = document.createElement('canvas');
      copy.width = this.sigPad.width;
      copy.height = this.sigPad.height;
      copy.getContext('2d').drawImage(this.sigPad, 0, 0);
      // then trim it
      trimCanvas(copy);

      if (copy.width > 1 && copy.height > 1) {
        onSave(copy.toDataURL('image/png'));
      } else {
        onSave('');
      }
    }
  }

  handleInput(newValue) {
    this.setState({ typedName: newValue });
  }

  render() {
    const { onCancel } = this.props;
    const { typedName } = this.state;
    return (
      <div>
        <VerticalField label="Type Name" className="mt-4">
          <InputText
            icon="signature"
            value={typedName}
            onChange={this.handleInput}
            inputProps={{ autoFocus: true }}
          />
        </VerticalField>
        <VerticalField label="Preview" className="mt-4">
          <div className="relative bg-gray-50 border rounded-lg shadow-inner">
            <div className="absolute w-full px-4 py-8 pointer-events-none bottom-0">
              <div className="w-full border-b-2 border-cool-gray-800">
                <XIcon className="h-8 w-8 flex-0" />
              </div>
            </div>
            <div style={{ marginLeft: 52, marginBottom: 29 }}>
              <canvas ref={this.setSigPadRef} style={{ width: 300, height: 100 }} />
            </div>
          </div>
        </VerticalField>

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <span className="flex w-full rounded-md shadow-sm sm:col-start-2">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              onClick={this.handleSave}
            >
              Save
            </button>
          </span>
          <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:col-start-1">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              onClick={onCancel}
            >
              Cancel
            </button>
          </span>
        </div>
      </div>
    );
  }
}

/**
 * DrawSignature.
 */

/** @typedef {{
  onSave: (base64png: string) => void,
  onCancel: () => void
}} DrawSignatureProps */

/** @type { React.ValidationMap<DrawSignatureProps> } */
const drawSignaturePropTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/** @augments { Component<DrawSignatureProps> } */
class DrawSignature extends Component {
  static propTypes = drawSignaturePropTypes;

  constructor(props) {
    super(props);
    this.setSigPadRef = this.setSigPadRef.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleClear = this.handleClear.bind(this);
  }

  setSigPadRef(canvasRef) {
    this.sigPad = canvasRef;
  }

  handleSave() {
    const { onSave } = this.props;

    if (this.sigPad) {
      const trimmedCanvas = this.sigPad.getTrimmedCanvas();
      if (trimmedCanvas.width > 1 && trimmedCanvas.height > 1) {
        onSave(trimmedCanvas.toDataURL('image/png'));
      } else {
        onSave('');
      }
    }
  }

  handleClear() {
    if (this.sigPad) {
      this.sigPad.clear();
    }
  }

  render() {
    const { onCancel } = this.props;
    return (
      <div className="pt-6">
        <div className="relative bg-cool-gray-50 border rounded-lg shadow-inner">
          <div className="absolute w-full px-4 py-8 pointer-events-none bottom-0">
            <div className="w-full border-b-2 border-cool-gray-800">
              <XIcon className="h-8 w-8 flex-0" />
            </div>
          </div>
          <ReactSignaturePad
            ref={this.setSigPadRef}
            canvasProps={{ style: { width: 300, height: 200 } }}
          />
        </div>
        <div className="p-2 text-center">
          <button
            type="button"
            className="tw-btn tw-btn-sm tw-btn-blue-alt"
            onClick={this.handleClear}
          >
            Clear
          </button>
        </div>
        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <span className="flex w-full rounded-md shadow-sm sm:col-start-2">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              onClick={this.handleSave}
            >
              Save
            </button>
          </span>
          <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:col-start-1">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline transition ease-in-out duration-150 sm:text-sm sm:leading-5"
              onClick={onCancel}
            >
              Cancel
            </button>
          </span>
        </div>
      </div>
    );
  }
}

/**
 * SignaturePadModal.
 */

/** @typedef {{
  isOpen: boolean,
  onClose: () => void,
  onSave: (base64png: string) => void
}} SignaturePadModalProps */

/** @typedef {{
  currentTab: "DRAW" | "TYPE"
}} SignaturePadModalState */

/** @type { React.ValidationMap<SignaturePadModalProps> } */
const signaturePadModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

/** @augments { Component<SignaturePadModalProps, SignaturePadModalState> } */
class SignaturePadModal extends Component {
  static propTypes = signaturePadModalPropTypes;

  constructor(props) {
    super(props);
    /** @type SignaturePadModalState */
    this.state = { currentTab: 'DRAW' };
    this.handleSave = this.handleSave.bind(this);
    this.setCurrentTab = this.setCurrentTab.bind(this);
  }

  setCurrentTab(tab) {
    this.setState({ currentTab: tab });
  }

  handleSave(base64png) {
    const { onSave, onClose } = this.props;
    onSave(base64png);
    onClose();
  }

  render() {
    const { isOpen, onClose } = this.props;
    const { currentTab } = this.state;

    return (
      <Modal show={isOpen} onHide={onClose}>
        <ModalHeader icon="signature" title="Sign" onClose={onClose} />
        <div className="px-4 pt-5 pb-4 overflow-hidden">
          {/* <div className="flex items-center justify-around">
            <Tab active={currentTab === 'DRAW'} onClick={() => this.setCurrentTab('DRAW')}>
              Draw Signature
            </Tab>
            <p className="px-4 text-xs text-cool-gray-500">OR</p>
            <Tab active={currentTab === 'TYPE'} onClick={() => this.setCurrentTab('TYPE')}>
              Type Signature
            </Tab>
          </div> */}
          {currentTab === 'DRAW' ? (
            <DrawSignature onSave={this.handleSave} onCancel={onClose} />
          ) : (
            <TypeSignature onSave={this.handleSave} onCancel={onClose} />
          )}
        </div>
      </Modal>
    );
  }
}

const codes = {
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  END: 35,
  HOME: 36,
};

/**
 * SignaturePad.
 */

/** @typedef {{
  id?: string,
  value: string,
  onChange?: (base64png: string) => void,
  onBlur?: (e: any) => void,
  editable?: boolean,
  className?: string
}} SignaturePadProps */

/** @typedef {{
  modalOpen: boolean
}} SignaturePadState */

/** @type { React.ValidationMap<SignaturePadProps> } */
const signaturePadPropTypes = {
  id: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  editable: PropTypes.bool,
  className: PropTypes.string,
};

/** @augments { React.Component<SignaturePadProps, SignaturePadState> } */
export class SignaturePad extends Component {
  static propTypes = signaturePadPropTypes;

  constructor(props) {
    super(props);
    this.state = { modalOpen: false };
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  openModal() {
    this.setState({ modalOpen: true });
  }

  closeModal() {
    this.setState({ modalOpen: false });
  }

  handleChange(base64png) {
    const { onChange } = this.props;
    if (onChange) return onChange(base64png);
  }

  handleKeyDown(event) {
    const { modalOpen } = this.state;
    if (!modalOpen) {
      event.persist();
      let flag = false;

      switch (event.keyCode) {
        case codes.SPACE:
        case codes.RETURN:
          this.openModal();
          flag = true;
          break;
        case codes.ESC:
        case codes.END:
          this.handleChange('');
          flag = true;
          break;
        default:
          break;
      }

      if (flag) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
  }

  render() {
    const { id, value, editable = false, onBlur, className = '' } = this.props;
    const { modalOpen } = this.state;

    return (
      <>
        <SignaturePadModal
          isOpen={modalOpen}
          onClose={this.closeModal}
          onSave={this.handleChange}
        />
        <div id={id} className={`_SignaturePad ${className}`}>
          <div className="border-b-2 border-gray-800 flex items-end">
            <XIcon className="h-8 w-8 flex-0" />
            {!!value ? (
              <div className="flex-1 p-1">
                <img className="max-w-full" src={value} alt="signature" />
              </div>
            ) : (
              <div
                tabIndex={editable ? 0 : -1}
                className={`_click-to-sign flex-1 m-1 px-4 py-6 text-center text-blue-500 font-bold cursor-pointer focus:outline-none focus:shadow-outline rounded-xl border-2 border-dashed border-blue-300`}
                onClick={editable ? this.openModal : undefined}
                onKeyDown={editable ? this.handleKeyDown : undefined}
                onBlur={onBlur}
              >
                <p className="flex items-center justify-center">
                  <PencilIcon className="mr-1 text-blue-400 w-6 h-6" />
                  <span>Click to sign</span>
                </p>
              </div>
            )}
          </div>
          {value && editable && (
            <div className="text-center p-2">
              <button
                type="button"
                className="tw-btn tw-btn-blue-alt tw-btn-sm mx-auto"
                onClick={this.openModal}
              >
                Click to edit
              </button>
            </div>
          )}
        </div>
      </>
    );
  }
}

function fitTextOnCanvas(canvas, text, fontFace) {
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // start with large font size
    let fontSize = 32;

    // lower the font size until the text fits the canvas
    do {
      fontSize--;
      ctx.font = `${fontSize}px ${fontFace}`;
    } while (ctx.measureText(text).width > canvas.width || fontSize > canvas.height);

    // draw the text
    ctx.fillText(text, 0, canvas.height - fontSize);
  }
}

/* eslint-enable max-len */
